import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoon } from '@fortawesome/pro-light-svg-icons/faMoon';
import { faMobile } from '@fortawesome/pro-light-svg-icons/faMobile';
import { faArrowRightFromBracket } from '@fortawesome/pro-light-svg-icons/faArrowRightFromBracket';
import { faWandMagicSparkles } from '@fortawesome/pro-light-svg-icons/faWandMagicSparkles';
import { TypedMemo } from '../../../common/types';
import { useUserToken } from '../../../../redux/slices/appInfo/hooks';
import { useOnLogout } from '../../../../redux/slices/appInfo/useOnLogout';
import { SMLLink } from '../../components/SMLLink/SMLLink';
import { withBrandWord } from '../../components/primitives/BrandWord';
import { openWishBox } from '../../wishBox/wishBoxStore';
import { wishBoxMessages } from '../../wishBox/wishBoxMessages';
import { useAccountMenuLinks } from './useAccountMenuLinks';
import { AccountThemeSegment } from './AccountThemeSegment';
import { accountMenuMessages as messages } from './accountMenuMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the account menu and sheet */
import styles from './AccountMenu.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface AccountMenuListProps {
  /** The social accounts that need a new sign-in: the red count of Connections. */
  accountAttention: number;
  /** A link opens a page: the menu or the sheet closes. */
  onNavigate: () => void;
  /** "Get the mobile app": the owner of the menu closes it and opens the window. */
  onOpenMobileApp: () => void;
  /** `sheet`: the taller rows of the mobile account sheet (design 7d). */
  variant?: 'menu' | 'sheet';
}

/**
 * design: navigation-v2 6a, 7d. The rows of the account menu: Settings, Connections, Plans and
 * billing, then Theme, Get the mobile app, Help and support, Review MUSIC24 (design review-form
 * a11), "Tell us what is missing" (design wish-box a5), then Log out.
 */
const AccountMenuListComponent: React.FC<AccountMenuListProps> = ({
  accountAttention,
  onNavigate,
  onOpenMobileApp,
  variant = 'menu',
}) => {
  const intl = useIntl();
  const onLogout = useOnLogout(useUserToken());
  const links = useAccountMenuLinks(accountAttention);
  const help = useMemo(() => links.find((link) => link.key === 'help'), [links]);
  const review = useMemo(() => links.find((link) => link.key === 'review'), [links]);
  const topLinks = useMemo(() => links.filter((link) => link.key !== 'help' && link.key !== 'review'), [links]);
  const rowClass = clsx(styles.row, variant === 'sheet' && styles.sheetRow);

  const renderLink = (link: (typeof links)[number]) => (
    <li key={link.key}>
      <SMLLink to={link.route} params={link.params} className={rowClass} onNavigate={onNavigate}>
        <FontAwesomeIcon icon={link.icon} className={styles.rowIcon} fixedWidth />
        <span className={styles.rowLabel}>{withBrandWord(link.label)}</span>
        {link.count !== undefined && (
          <>
            <span className={styles.count} aria-hidden="true" data-testid="account-menu-count">
              {link.count}
            </span>
            <span className="visually-hidden">{link.countText}</span>
          </>
        )}
      </SMLLink>
    </li>
  );

  return (
    <ul className={styles.list}>
      {topLinks.map(renderLink)}
      <li className={styles.separator} aria-hidden="true" />
      <li className={clsx(rowClass, styles.staticRow)}>
        <FontAwesomeIcon icon={faMoon} className={styles.rowIcon} fixedWidth />
        <span className={styles.rowLabel}>{intl.formatMessage(messages.theme)}</span>
        <AccountThemeSegment />
      </li>
      <li>
        <button type="button" className={rowClass} onClick={onOpenMobileApp} aria-haspopup="dialog">
          <FontAwesomeIcon icon={faMobile} className={styles.rowIcon} fixedWidth />
          <span className={styles.rowLabel}>{intl.formatMessage(messages.mobileApp)}</span>
        </button>
      </li>
      {help !== undefined && renderLink(help)}
      {review !== undefined && renderLink(review)}
      <li>
        <button
          type="button"
          className={rowClass}
          onClick={() => {
            onNavigate();
            openWishBox('account');
          }}
          aria-haspopup="dialog"
        >
          <FontAwesomeIcon icon={faWandMagicSparkles} className={styles.rowIcon} fixedWidth />
          <span className={styles.rowLabel}>{intl.formatMessage(wishBoxMessages.entry)}</span>
        </button>
      </li>
      <li className={styles.separator} aria-hidden="true" />
      <li>
        <button type="button" className={rowClass} onClick={onLogout}>
          <FontAwesomeIcon icon={faArrowRightFromBracket} className={styles.rowIcon} fixedWidth />
          <span className={styles.rowLabel}>{intl.formatMessage(messages.logOut)}</span>
        </button>
      </li>
    </ul>
  );
};

export const AccountMenuList = TypedMemo(AccountMenuListComponent);
